//TODO : Refactor after ai
/**
 * Modul 6 – Baustelle (mobil): Prüfen → Start → Zeiten → Material → Fotos → Ausmass → Abschluss.
 * Ein Schritt je Bildschirm, grosse Touchflächen. Annahmen: ANNAHMEN C7, C8, C9.
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDatenStore } from "@app/store";
import {
  ausmassAusAuftrag,
  datensatz,
  datensaetze,
  heute,
  pruefeAusfuehrung,
  rapportAusEinsatz,
} from "@shared/demo-data/viacid";
import {
  datumCH,
  fr,
  mit,
  num,
  str,
  viele,
  zahl,
  ziel,
  neueId,
  jetztIso,
} from "./lib";
import {
  Feld,
  Hinweis,
  Karte,
  Knopf,
  Pille,
  Schritte,
  Seite,
  Zeile,
} from "./ui";

const SCHRITTE = [
  "Prüfen",
  "Start",
  "Zeiten",
  "Material",
  "Fotos",
  "Ausmass",
  "Abschluss",
];

export function BaustelleListePage() {
  const b = useDatenStore((s) => s.bestand);
  const liste = [...datensaetze(b, "einsatz")].sort((x, y) =>
    str(x.werte.einsatzdatum).localeCompare(str(y.werte.einsatzdatum))
  );
  return (
    <Seite titel="Baustelle" untertitel="Einsätze – antippen und loslegen">
      <div className="vc-mobile vc-card vc-list">
        {liste.map((e) => (
          <Zeile
            key={e.id}
            titel={e.titel}
            meta={`${datumCH(e.werte.einsatzdatum)} · ${str(e.werte.zeitslot)} · ${ziel(b, e, "einsatz-am-strassenobjekt", "strassenobjekt")?.titel ?? ""}`}
            rechts={<Pille status={e.status} />}
            zu={`/app/baustelle/${e.id}`}
          />
        ))}
      </div>
    </Seite>
  );
}

export function BaustellePage() {
  const { id = "" } = useParams();
  const b = useDatenStore((s) => s.bestand);
  const aendern = useDatenStore((s) => s.aendern);
  const anlegen = useDatenStore((s) => s.anlegen);
  const anwenden = useDatenStore((s) => s.anwenden);
  const melden = useDatenStore((s) => s.melden);
  const rolle = useDatenStore((s) => s.rolle);
  const darfFreigeben = rolle === "GF";
  const nav = useNavigate();
  const e = datensatz(b, "einsatz", id);
  const [schritt, setSchritt] = useState(() =>
    e?.status === "Ausgeführt"
      ? 6
      : e?.status === "Ausführung freigegeben"
        ? 1
        : 0
  );
  const [pruef, setPruef] = useState({
    boden: str(e?.werte.bodentemperatur),
    luft: str(e?.werte.lufttemperatur),
    feuchte: str(e?.werte.luftfeuchtigkeit),
    trocken: e?.werte.oberflaecheTrocken !== false,
    sauber: e?.werte.oberflaecheSauber !== false,
  });
  const [zeit, setZeit] = useState({
    ma: "",
    beginn: "07:00",
    ende: "11:45",
    pause: "0.5",
    taetigkeit: "Rissverguss",
    stunden: "1.5",
  });
  const [mat, setMat] = useState({
    artikel: "",
    entnommen: "10",
    verbraucht: "8",
  });
  const [ausmass, setAusmass] = useState<Record<string, string>>({});

  if (!e) return <Hinweis ton="red">Einsatz nicht gefunden.</Hinweis>;
  const auftrag = ziel(b, e, "einsatz-fuer-auftrag", "auftrag");
  const strasse = ziel(b, e, "einsatz-am-strassenobjekt", "strassenobjekt");
  const kunde = ziel(b, auftrag, "auftrag-von-kunde", "kunde");
  const team = viele(e.bezuege["einsatz-team"])
    .map((mid) => datensatz(b, "mitarbeiter", mid))
    .filter(Boolean);
  const fahrzeuge = viele(e.bezuege["einsatz-nutzt-fahrzeug"])
    .map((fid) => datensatz(b, "fahrzeug", fid))
    .filter(Boolean);
  const positionen = auftrag
    ? mit(
        b,
        "auftragsposition",
        "auftragsposition-gehoert-zu-auftrag",
        auftrag.id
      )
    : [];
  const massnahmen = positionen
    .map((p) =>
      ziel(b, p, "auftragsposition-umfasst-massnahmenart", "massnahmenart")
    )
    .filter(Boolean);
  const minTemp = Math.max(
    0,
    ...massnahmen.map((m) => num(m?.werte.mindesttemperaturBoden))
  );
  const zeilen = mit(b, "rapportzeile", "rapportzeile-zu-einsatz", e.id);
  const bewegungen = auftrag
    ? mit(b, "materialbewegung", "materialbewegung-fuer-auftrag", auftrag.id)
    : [];
  const fotos = mit(
    b,
    "baustellenfoto",
    "baustellenfoto-dokumentiert-einsatz",
    e.id
  );
  const artikel = datensaetze(b, "artikel");
  const fertig =
    e.status === "Ausgeführt" || e.status === "Ausführung abgesagt";

  const pruefwerteSpeichern = () =>
    aendern({
      slug: "einsatz",
      id: e.id,
      werte: {
        bodentemperatur: Number(pruef.boden),
        lufttemperatur: Number(pruef.luft),
        luftfeuchtigkeit: Number(pruef.feuchte),
        oberflaecheTrocken: pruef.trocken,
        oberflaecheSauber: pruef.sauber,
        geprueftAm: jetztIso(),
      },
    });
  const freigeben = () => {
    if (!darfFreigeben)
      return melden(
        "Freigeben darf nur der Gruppenführer – Ansicht als Gruppenführer wählen.",
        true
      );
    pruefwerteSpeichern();
    const fehler = pruefeAusfuehrung(useDatenStore.getState().bestand, e.id);
    if (fehler) {
      melden(fehler, true);
      return;
    }
    aendern(
      { slug: "einsatz", id: e.id, status: "Ausführung freigegeben" },
      "Prüfwerte in Ordnung – Ausführung freigegeben."
    );
    setSchritt(1);
  };
  const absagen = () => {
    pruefwerteSpeichern();
    aendern(
      {
        slug: "einsatz",
        id: e.id,
        status: "Ausführung abgesagt",
        werte: {
          begruendungAusfuehrungsentscheidung: "Prüfwerte nicht erfüllt",
        },
      },
      "Einsatz abgesagt – Disposition informiert."
    );
    nav("/app/baustelle");
  };
  const zeileHinzufuegen = (art: "Arbeitszeit" | "Tätigkeit") => {
    const m = datensatz(b, "mitarbeiter", zeit.ma) ?? team[0];
    const stunden =
      art === "Arbeitszeit"
        ? Math.max(
            0,
            (Number(zeit.ende.slice(0, 2)) * 60 + Number(zeit.ende.slice(3))) /
              60 -
              (Number(zeit.beginn.slice(0, 2)) * 60 +
                Number(zeit.beginn.slice(3))) /
                60 -
              Number(zeit.pause)
          )
        : Number(zeit.stunden);
    anlegen(
      "rapportzeile",
      {
        id: neueId("rz"),
        titel:
          art === "Arbeitszeit"
            ? `Arbeitszeit ${m?.titel ?? ""}`
            : zeit.taetigkeit,
        untertitel: `${zahl(stunden)} h`,
        kennung: null,
        status: null,
        werte:
          art === "Arbeitszeit"
            ? {
                zeilenart: "Arbeitszeit",
                beginn: zeit.beginn,
                ende: zeit.ende,
                pause: Number(zeit.pause),
                stunden: Math.round(stunden * 4) / 4,
              }
            : {
                zeilenart: "Tätigkeit",
                stunden: Math.round(stunden * 4) / 4,
                bemerkung: zeit.taetigkeit,
              },
        bezuege: {
          "rapportzeile-zu-einsatz": e.id,
          ...(m ? { "rapportzeile-von-mitarbeiter": m.id } : {}),
        },
      },
      "Rapportzeile erfasst (viertelstundengenau)."
    );
  };
  const materialBuchen = () => {
    const ar = artikel.find((x) => x.id === mat.artikel) ?? artikel[0];
    if (!ar || !auftrag) return;
    const ent = Number(mat.entnommen),
      ver = Number(mat.verbraucht);
    const basis = {
      kennung: null,
      status: "Gemeldet",
      bezuege: {
        "materialbewegung-fuer-auftrag": auftrag.id,
        "materialbewegung-fuer-einsatz": e.id,
        "materialbewegung-betrifft-artikel": ar.id,
      },
    };
    anlegen("materialbewegung", {
      ...basis,
      id: neueId("mb", "-1"),
      titel: `Entnahme ${ar.titel}`,
      untertitel: `${ent} ${str(ar.werte.gebindeeinheit)}`,
      werte: {
        bewegungsart: "Entnahme",
        menge: ent,
        einheit: str(ar.werte.gebindeeinheit),
        zeitpunkt: heute(),
      },
    });
    anlegen("materialbewegung", {
      ...basis,
      id: neueId("mb", "-2"),
      titel: `Verbrauch ${ar.titel}`,
      untertitel: `${ver} ${str(ar.werte.gebindeeinheit)}`,
      werte: {
        bewegungsart: "Verbrauch",
        menge: ver,
        einheit: str(ar.werte.gebindeeinheit),
        zeitpunkt: heute(),
      },
    });
    if (ent > ver)
      anlegen(
        "materialbewegung",
        {
          ...basis,
          id: neueId("mb", "-3"),
          titel: `Rückgabe ${ar.titel}`,
          untertitel: `${ent - ver} ${str(ar.werte.gebindeeinheit)}`,
          werte: {
            bewegungsart: "Rückgabe",
            menge: ent - ver,
            einheit: str(ar.werte.gebindeeinheit),
            zeitpunkt: heute(),
          },
        },
        `Material gebucht: ${ent} entnommen, ${ver} verbraucht, ${ent - ver} retour.`
      );
    else melden(`Material gebucht: ${ent} entnommen, ${ver} verbraucht.`);
  };
  const fotoNachher = () =>
    anlegen(
      "baustellenfoto",
      {
        id: neueId("bf"),
        titel: "Foto nachher",
        untertitel: heute(),
        kennung: null,
        status: null,
        werte: {
          fotoart: "Nachher",
          aufnahmezeitpunkt: heute(),
          beschreibung: strasse?.titel ?? "",
        },
        bezuege: { "baustellenfoto-dokumentiert-einsatz": e.id },
      },
      "Foto (Platzhalter) gespeichert."
    );
  const abschliessen = () => {
    for (const p of positionen) {
      const menge = Number(ausmass[p.id]);
      if (menge)
        aendern({
          slug: "auftragsposition",
          id: p.id,
          werte: { ausgefuehrteMenge: menge },
        });
    }
    anwenden(rapportAusEinsatz(useDatenStore.getState().bestand, e.id));
    aendern({ slug: "einsatz", id: e.id, status: "Ausgeführt" });
    if (auftrag)
      anwenden(ausmassAusAuftrag(useDatenStore.getState().bestand, auftrag.id));
    melden(
      "Einsatz abgeschlossen – Rapport und Ausmass erstellt, Kunde kann freigeben."
    );
    setSchritt(6);
  };

  return (
    <div className="vc-mobile">
      <Seite
        titel={auftrag?.titel ?? e.titel}
        untertitel={
          <>
            {kunde?.titel} · {strasse?.titel} · {datumCH(e.werte.einsatzdatum)}{" "}
            {str(e.werte.zeitslot)} <Pille status={e.status} />
          </>
        }
      >
        <Schritte anzahl={SCHRITTE.length} aktiv={schritt} />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: "var(--vc-muted)",
            marginBottom: 12,
          }}
        >
          <span>
            Schritt {schritt + 1} von {SCHRITTE.length}:{" "}
            <strong>{SCHRITTE[schritt]}</strong>
          </span>
          <span>
            {team.length} MA · {fahrzeuge.map((f) => f?.kennung).join(", ")}
          </span>
        </div>

        {schritt === 0 && (
          <Karte
            titel="Prüfwerte vor Beginn"
            sub={
              minTemp
                ? `Mindest-Bodentemperatur ${minTemp} °C (${massnahmen.map((m) => m?.titel).join(", ")})`
                : "Massnahmenbedingungen"
            }
          >
            <div className="vc-grid c3">
              <Feld label="Bodentemperatur °C">
                <input
                  type="number"
                  inputMode="decimal"
                  value={pruef.boden}
                  onChange={(ev) =>
                    setPruef({ ...pruef, boden: ev.target.value })
                  }
                />
              </Feld>
              <Feld label="Lufttemperatur °C">
                <input
                  type="number"
                  inputMode="decimal"
                  value={pruef.luft}
                  onChange={(ev) =>
                    setPruef({ ...pruef, luft: ev.target.value })
                  }
                />
              </Feld>
              <Feld label="Luftfeuchte %">
                <input
                  type="number"
                  inputMode="numeric"
                  value={pruef.feuchte}
                  onChange={(ev) =>
                    setPruef({ ...pruef, feuchte: ev.target.value })
                  }
                />
              </Feld>
            </div>
            <div style={{ display: "flex", gap: 10, margin: "14px 0" }}>
              <Knopf
                art={pruef.trocken ? "primary" : undefined}
                onClick={() => setPruef({ ...pruef, trocken: !pruef.trocken })}
              >
                {pruef.trocken ? "✓" : "○"} Oberfläche trocken
              </Knopf>
              <Knopf
                art={pruef.sauber ? "primary" : undefined}
                onClick={() => setPruef({ ...pruef, sauber: !pruef.sauber })}
              >
                {pruef.sauber ? "✓" : "○"} Oberfläche sauber
              </Knopf>
            </div>
            {!fertig && (
              <div className="vc-grid c2">
                <Knopf gross onClick={absagen}>
                  Nein – absagen
                </Knopf>
                <Knopf gross art="primary" onClick={freigeben}>
                  Ja – ausführen
                </Knopf>
              </div>
            )}
            {fertig && (
              <Knopf gross onClick={() => setSchritt(6)}>
                Zum Abschluss
              </Knopf>
            )}
          </Karte>
        )}

        {schritt === 1 && (
          <Karte titel="Auftragsblatt" sub="Alles, was das Team jetzt braucht">
            <div className="vc-list">
              <Zeile
                titel="Adresse"
                meta={
                  <>
                    {str(strasse?.werte.adresse)} ·{" "}
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(str(strasse?.werte.adresse))}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "var(--vc-orange)" }}
                    >
                      Google Maps
                    </a>
                  </>
                }
              />
              {positionen.map((p) => (
                <Zeile
                  key={p.id}
                  titel={str(p.werte.bezeichnung)}
                  meta={`${num(p.werte.menge)} ${str(p.werte.einheit)}`}
                />
              ))}
              <Zeile
                titel="Team"
                meta={team.map((m) => m?.titel).join(", ") || "—"}
              />
              <Zeile
                titel="Hinweise"
                meta={
                  str(e.werte.einsatzhinweise) ||
                  str(strasse?.werte.zufahrtshinweise) ||
                  "—"
                }
              />
            </div>
            <Knopf gross art="primary" onClick={() => setSchritt(2)}>
              Ausführung gestartet
            </Knopf>
          </Karte>
        )}

        {schritt === 2 && (
          <Karte
            titel="Zeiten"
            sub="Arbeitszeit je Mitarbeiter (gesetzlich) und Tätigkeiten viertelstundengenau"
          >
            <Feld label="Mitarbeiter">
              <select
                value={zeit.ma}
                onChange={(ev) => setZeit({ ...zeit, ma: ev.target.value })}
              >
                {team.map((m) => (
                  <option key={m?.id} value={m?.id}>
                    {m?.titel}
                  </option>
                ))}
              </select>
            </Feld>
            <div className="vc-grid c3" style={{ marginTop: 8 }}>
              <Feld label="Beginn">
                <input
                  type="time"
                  value={zeit.beginn}
                  onChange={(ev) =>
                    setZeit({ ...zeit, beginn: ev.target.value })
                  }
                />
              </Feld>
              <Feld label="Ende">
                <input
                  type="time"
                  value={zeit.ende}
                  onChange={(ev) => setZeit({ ...zeit, ende: ev.target.value })}
                />
              </Feld>
              <Feld label="Pause h">
                <input
                  type="number"
                  step="0.25"
                  value={zeit.pause}
                  onChange={(ev) =>
                    setZeit({ ...zeit, pause: ev.target.value })
                  }
                />
              </Feld>
            </div>
            <div style={{ marginTop: 8 }}>
              <Knopf onClick={() => zeileHinzufuegen("Arbeitszeit")}>
                + Arbeitszeit buchen
              </Knopf>
            </div>
            <div className="vc-grid c2" style={{ marginTop: 14 }}>
              <Feld label="Tätigkeit">
                <select
                  value={zeit.taetigkeit}
                  onChange={(ev) =>
                    setZeit({ ...zeit, taetigkeit: ev.target.value })
                  }
                >
                  {[
                    ...new Set([
                      ...massnahmen.map((m) => str(m?.titel)),
                      "Signalisation",
                      "Anfahrt",
                      "Belagsaufbruch",
                    ]),
                  ].map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </Feld>
              <Feld label="Stunden (¼ h)">
                <input
                  type="number"
                  step="0.25"
                  value={zeit.stunden}
                  onChange={(ev) =>
                    setZeit({ ...zeit, stunden: ev.target.value })
                  }
                />
              </Feld>
            </div>
            <div style={{ marginTop: 8 }}>
              <Knopf onClick={() => zeileHinzufuegen("Tätigkeit")}>
                + Tätigkeit buchen
              </Knopf>
            </div>
            <div className="vc-list" style={{ marginTop: 12 }}>
              {zeilen.map((z) => (
                <Zeile
                  key={z.id}
                  titel={z.titel}
                  meta={`${str(z.werte.zeilenart)} · ${zahl(num(z.werte.stunden))} h${z.werte.beginn ? ` · ${str(z.werte.beginn)}–${str(z.werte.ende)}` : ""}`}
                />
              ))}
            </div>
            <Knopf gross art="primary" onClick={() => setSchritt(3)}>
              Weiter zu Material
            </Knopf>
          </Karte>
        )}

        {schritt === 3 && (
          <Karte
            titel="Material"
            sub="Entnommen · verbraucht · retour – je Auftrag nachvollziehbar"
          >
            <Feld label="Artikel">
              <select
                value={mat.artikel}
                onChange={(ev) => setMat({ ...mat, artikel: ev.target.value })}
              >
                {artikel.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.titel}
                  </option>
                ))}
              </select>
            </Feld>
            <div className="vc-grid c2" style={{ marginTop: 8 }}>
              <Feld label="Entnommen (Gebinde)">
                <input
                  type="number"
                  inputMode="numeric"
                  value={mat.entnommen}
                  onChange={(ev) =>
                    setMat({ ...mat, entnommen: ev.target.value })
                  }
                />
              </Feld>
              <Feld label="Verbraucht (Gebinde)">
                <input
                  type="number"
                  inputMode="numeric"
                  value={mat.verbraucht}
                  onChange={(ev) =>
                    setMat({ ...mat, verbraucht: ev.target.value })
                  }
                />
              </Feld>
            </div>
            <div className="sub" style={{ margin: "8px 0" }}>
              Retour:{" "}
              {Math.max(0, Number(mat.entnommen) - Number(mat.verbraucht))}
            </div>
            <Knopf onClick={materialBuchen}>Material buchen</Knopf>
            <div className="vc-list" style={{ marginTop: 12 }}>
              {bewegungen.map((m) => (
                <Zeile
                  key={m.id}
                  titel={m.titel}
                  meta={`${str(m.werte.bewegungsart)} · ${num(m.werte.menge)} ${str(m.werte.einheit)}`}
                  rechts={<Pille status={m.status} />}
                />
              ))}
            </div>
            <Knopf gross art="primary" onClick={() => setSchritt(4)}>
              Weiter zu Fotos
            </Knopf>
          </Karte>
        )}

        {schritt === 4 && (
          <Karte
            titel="Fotos"
            sub="Nachher-Fotos für Rapport und Qualitätsnachweis"
          >
            <Knopf gross onClick={fotoNachher}>
              📷 Foto nachher aufnehmen
            </Knopf>
            <div className="vc-list" style={{ marginTop: 12 }}>
              {fotos.map((f) => (
                <Zeile
                  key={f.id}
                  titel={f.titel}
                  meta={`${str(f.werte.fotoart)} · ${datumCH(f.werte.aufnahmezeitpunkt)}`}
                />
              ))}
            </div>
            <Knopf gross art="primary" onClick={() => setSchritt(5)}>
              Weiter zum Ausmass
            </Knopf>
          </Karte>
        )}

        {schritt === 5 && (
          <Karte
            titel="Ausmass"
            sub="Ausgeführte Menge je Position – daraus entstehen Rapport und Ausmassprotokoll"
          >
            {positionen.map((p) => (
              <Feld
                key={p.id}
                label={`${str(p.werte.bezeichnung)} (Auftrag ${num(p.werte.menge)} ${str(p.werte.einheit)})`}
              >
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder={String(num(p.werte.menge))}
                  value={ausmass[p.id] ?? ""}
                  onChange={(ev) =>
                    setAusmass({ ...ausmass, [p.id]: ev.target.value })
                  }
                />
              </Feld>
            ))}
            <div style={{ height: 12 }} />
            <Knopf gross art="primary" onClick={abschliessen} disabled={fertig}>
              Einsatz abschliessen
            </Knopf>
          </Karte>
        )}

        {schritt === 6 && (
          <Karte
            titel="Abgeschlossen"
            sub="Rapport und Ausmass liegen beim Innendienst"
          >
            <Hinweis ton="green">
              Arbeitsrapport erstellt · {zeilen.length} Rapportzeilen ·{" "}
              {bewegungen.length} Materialbewegungen · {fotos.length} Fotos. Das
              Ausmass wartet auf die Kundenfreigabe.
            </Hinweis>
            <div style={{ height: 12 }} />
            {auftrag && (
              <Knopf
                gross
                art="primary"
                onClick={() => nav(`/app/auftraege/${auftrag.id}`)}
              >
                Zum Auftrag {auftrag.kennung}
              </Knopf>
            )}
            <div style={{ height: 8 }} />
            <Knopf gross onClick={() => nav("/app/baustelle")}>
              Zurück zur Liste
            </Knopf>
          </Karte>
        )}

        {schritt > 0 && schritt < 6 && (
          <div style={{ marginTop: 10 }}>
            <Knopf art="ghost" onClick={() => setSchritt(schritt - 1)}>
              ◀ Zurück
            </Knopf>
          </div>
        )}
        <p className="sub" style={{ marginTop: 10 }}>
          Rechnung später:{" "}
          {auftrag
            ? fr(
                positionen.reduce(
                  (s, p) => s + num(p.werte.menge) * num(p.werte.einzelpreis),
                  0
                )
              )
            : "—"}{" "}
          laut Auftrag.
        </p>
      </Seite>
    </div>
  );
}
